import { createContext, type ReactNode, useContext } from "react";
import type { LinkComponent, LinkProps } from "./types";

const LinkComponentContext = createContext<LinkComponent | null>(null);

export function LinkProvider({
	component,
	children,
}: {
	component: LinkComponent;
	children: ReactNode;
}) {
	return (
		<LinkComponentContext.Provider value={component}>
			{children}
		</LinkComponentContext.Provider>
	);
}

/** An app-relative path such as `/kaart?city=x`, as opposed to `//host` or `https://`. */
function isInAppHref(href: string): boolean {
	return href.startsWith("/") && !href.startsWith("//");
}

/**
 * An anchor that navigates in-app links through the host app's router and
 * leaves external links, new tabs and downloads to the browser.
 */
export function Link({ href, target, download, ...props }: LinkProps) {
	const Component = useContext(LinkComponentContext);

	if (
		Component &&
		href !== undefined &&
		isInAppHref(href) &&
		target === undefined &&
		download === undefined
	) {
		return <Component href={href} {...props} />;
	}

	return <a href={href} target={target} download={download} {...props} />;
}
